import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Modal, Spin } from 'antd';
import { useSelector } from 'react-redux';
import { AvailableModals, closeModal } from './modalSlice';
import { ReduxState, useAppDispatch } from '../../store';
import ScopeDefinition from '../Configuration/ConfigurationStoresAndDepartments/ScopeDefinition';
import { setModalScope } from '../Configuration/ConfigurationStoresAndDepartments/scopeConfigurationSlice';
import { Text } from '@ydistri/ds';
import {
  transformToConfigurationRule,
  usePostConfigurationRuleMutation,
  usePutConfigurationRuleMutation,
} from '../Configuration/ConfigurationStoresAndDepartments/apiScopes';
import {
  isMainScope,
  localScopeProblem,
  ScopeModalType,
  validateScope,
  ViewMode,
} from '../Configuration/ConfigurationStoresAndDepartments/scopeUtilsLib';
import { mergeProblems, problemKey, problemsOfError } from '../Configuration/Validator/problemsLib';
import { ValidationProblemResponse } from '@ydistri/api-sdk';
import { useTemplateOrCalculationWithValue } from '../../hooks/useTemplateOrCalculationWithValue';
import styled from 'styled-components';

/** A disabled fieldset disables every control inside - the keyboard included, which a Spin does not. */
const Frozen = styled.fieldset`
  border: 0;
  margin: 0;
  padding: 0;
  min-width: 0;
`;

const modalId = AvailableModals.SCOPE_EDIT;

const ScopeEditModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const open = useSelector((state: ReduxState) => state.modal.openedModal === modalId);
  const scopeId = useSelector((state: ReduxState) => state.modal.modalData[modalId]?.scopeId ?? 0);

  const modalScope = useSelector((state: ReduxState) => state.scope.modalScope);
  const modalType = useSelector((state: ReduxState) => state.scope.modalType);
  const modalChanged = useSelector((state: ReduxState) => state.scope.modalChanged);

  const templateOrCalculation = useTemplateOrCalculationWithValue();
  const isCalculation = templateOrCalculation.type === 'Calculation';
  const [postConfigurationRuleMutation] = usePostConfigurationRuleMutation();
  const [putConfigurationRuleMutation] = usePutConfigurationRuleMutation();
  // What the server refused the last save with; the modal stays open until it is fixed.
  const [serverProblems, setServerProblems] = useState<ValidationProblemResponse[]>([]);
  // One save at a time: the form is frozen and the buttons wait until the answer is handled.
  const [saving, setSaving] = useState(false);
  const inFlight = useRef(false);

  const onCloseHandler = useCallback(() => {
    setServerProblems([]);
    dispatch(setModalScope(undefined));
    dispatch(closeModal());
  }, [dispatch]);

  const onSubmitHandler = useCallback(() => {
    if (inFlight.current) return;
    if (!modalScope) {
      onCloseHandler();
      return;
    }
    inFlight.current = true;
    setSaving(true);
    const configurationRule = transformToConfigurationRule(modalScope);
    const saved = modalScope.creating
      ? postConfigurationRuleMutation({
          templateId: templateOrCalculation.id,
          data: configurationRule,
        }).unwrap()
      : putConfigurationRuleMutation({
          id: modalScope.id,
          templateId: templateOrCalculation.id,
          data: configurationRule,
        }).unwrap();
    saved
      .then(onCloseHandler)
      .catch((error: unknown) => setServerProblems(problemsOfError(error)))
      .finally(() => {
        inFlight.current = false;
        setSaving(false);
      });
  }, [
    modalScope,
    onCloseHandler,
    postConfigurationRuleMutation,
    putConfigurationRuleMutation,
    templateOrCalculation.id,
  ]);

  const includeConfiguration = modalScope?.creating === true;

  const validationResult = useMemo(() => {
    return validateScope(modalScope, {
      includeConfiguration,
      includeScope: !isMainScope(modalScope),
    });
  }, [modalScope, includeConfiguration]);

  const okButtonProps = useMemo(
    () => ({ disabled: !validationResult.isValid || saving, loading: saving }),
    [saving, validationResult.isValid],
  );
  const cancelButtonProps = useMemo(
    () => ({ onClick: onCloseHandler, disabled: saving }),
    [onCloseHandler, saving],
  );

  // One map keyed by (code, target, path): the server's answer replaces the local copy with the same
  // identity, everything else it says stands next to it, and two answers with one code on two paths
  // both stay.
  const shownProblems = useMemo(() => {
    const local =
      modalScope && includeConfiguration && modalChanged
        ? localScopeProblem(modalScope, validationResult)
        : undefined;
    const keyed = (p: ValidationProblemResponse) => ({ ...p, key: problemKey(p) });
    return mergeProblems(local ? [keyed(local)] : [], serverProblems.map(keyed));
  }, [includeConfiguration, modalChanged, modalScope, serverProblems, validationResult]);

  return (
    <Modal
      width={800}
      title={modalScope?.creating ? 'Create new exception' : 'Edit exception scope'}
      open={open}
      cancelButtonProps={cancelButtonProps}
      closable={false}
      okText={modalScope?.creating ? 'Create' : 'Save'}
      okButtonProps={okButtonProps}
      onOk={onSubmitHandler}
    >
      {/* The form is frozen while the save is out: the request was built from the draft as it was */}
      <Spin spinning={saving}>
        <Frozen disabled={saving}>
          <ScopeDefinition
            scopeId={scopeId}
            viewMode={
              modalType === ScopeModalType.EDIT
                ? ViewMode.ADJUST_SCOPE_MODAL
                : ViewMode.NEW_EXCEPTION_MODAL
            }
            disabled={isCalculation}
            templateOrCalculation={templateOrCalculation}
          />
        </Frozen>
      </Spin>
      {shownProblems.map(p => (
        <Text key={p.key} $type="danger">
          {p.message}
        </Text>
      ))}
    </Modal>
  );
};

export default ScopeEditModal;
